import { FlexContainer, Grid, Rectangle, Typography } from '@pitchero/react-ui';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import Icon from '../icon';

/**
 * This calendar component is used within both the matches section along
 * with the calendar section.
 */
const CalendarComponent = ({
  month,
  year,
  data,
  validDates,
  onMonthClick,
  onDayClick,
  isMobile,
  isMatchesSection,
}) => {
  if (!data) return null;
  const currentMonth = DateTime.fromObject({ month, year });
  const calendar = data.map((item) => ({
    day: item.day,
    date: item.date,
    hidden: item.hidden,
    hasFixtures: item.fixtures.length > 0,
    hasTraining: item.trainingSessions.length > 0,
    hasEvents: item.clubEvents.length > 0 && !isMatchesSection,
    faded: DateTime.fromFormat(item.date, 'yyyy-MM-dd').month !== month,
  }));

  const dotColors = {
    training: '#46689F',
    fixture: '#68F06D',
    event: '#D273F0',
  };
  const dayDot = (type) => (
    <Rectangle
      radius="100%"
      style={{
        width: '8px',
        height: '8px',
        background: dotColors[type],
      }}
    />
  );
  return (
    <FlexContainer flexDirection="column" style={{ gap: '10px', width: '100%' }}>
      <Rectangle fill="#000" radius={isMobile && !isMatchesSection ? '0' : '10px'}>
        <FlexContainer
          style={{ padding: isMatchesSection ? '13px 0' : '20px 0', gap: '20px', width: '100%' }}
          alignItems="center"
        >
          <button type="button" onClick={() => onMonthClick(true)}>
            <FlexContainer alignItems="center" style={{ padding: '10px', gap: '5px' }}>
              <Icon name="arrow-left" color="#fff" size={20} />
              {!isMobile && !isMatchesSection && (
                <Typography size={18} color="#fff">
                  {currentMonth.minus({ months: 1 }).toFormat('MMMM')}
                </Typography>
              )}
            </FlexContainer>
          </button>
          <Typography
            color="#fff"
            preset="description"
            align="center"
            size={20}
            style={{ flex: 1 }}
          >
            {currentMonth.toFormat(isMobile || isMatchesSection ? 'MMM yyyy' : 'MMMM yyyy')}
          </Typography>
          <button type="button" onClick={() => onMonthClick(false)}>
            <FlexContainer alignItems="center" style={{ padding: '10px', gap: '5px' }}>
              {!isMobile && !isMatchesSection && (
                <Typography size={18} color="#fff">
                  {currentMonth.plus({ months: 1 }).toFormat('MMMM')}
                </Typography>
              )}
              <Icon name="arrow-right" color="#fff" size={20} />
            </FlexContainer>
          </button>
        </FlexContainer>
      </Rectangle>
      <Grid
        columns="repeat(7, 1fr)"
        style={{ width: '100%', marginTop: '10px' }}
        alignItems="center"
      >
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
          <Typography transform="uppercase" key={day} size={14} color="dustygray" align="center">
            {day}
          </Typography>
        ))}
      </Grid>
      <Grid
        columns="repeat(7, 1fr)"
        style={{
          width: '100%',
          gap: '1px',
          gridAutoRows: isMobile || isMatchesSection ? '64px' : '96px',
        }}
        alignItems="center"
      >
        {calendar.map((day) => {
          const isDayInRange = validDates.includes(day.date);
          return (
            <button
              key={day.date}
              type="button"
              style={{
                height: '100%',
                background: isDayInRange ? '#fff6e6' : 'transparent',
                outline: '1px solid',
                outlineColor: isDayInRange ? '#febb38' : '#e0e0e0',
                zIndex: isDayInRange ? 2 : 1,
              }}
              onClick={() => onDayClick(day.date)}
            >
              <FlexContainer
                key={day.date}
                flexDirection="column"
                alignItems="center"
                style={{
                  height: '100%',
                  boxSizing: 'border-box',
                  paddingTop: '8px',
                  gap: '8px',
                }}
              >
                <Typography color={day.faded ? '#b3b3b3' : 'black'}>{day.day}</Typography>
                <FlexContainer style={{ gap: '4px' }}>
                  {day.hasFixtures && dayDot('fixture')}
                  {day.hasTraining && dayDot('training')}
                  {day.hasEvents && dayDot('event')}
                </FlexContainer>
              </FlexContainer>
            </button>
          );
        })}
      </Grid>
      <FlexContainer alignItems="center" justifyContent="center" style={{ gap: '20px' }}>
        <FlexContainer alignItems="center" style={{ gap: '5px' }}>
          {dayDot('fixture')}
          <Typography weight="bold">Match</Typography>
        </FlexContainer>
        <FlexContainer alignItems="center" style={{ gap: '5px' }}>
          {dayDot('training')}
          <Typography weight="bold">Training</Typography>
        </FlexContainer>
        {!isMatchesSection && (
          <FlexContainer alignItems="center" style={{ gap: '5px' }}>
            {dayDot('event')}
            <Typography weight="bold">Event</Typography>
          </FlexContainer>
        )}
      </FlexContainer>
    </FlexContainer>
  );
};

CalendarComponent.propTypes = {
  month: PropTypes.number.isRequired,
  year: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  validDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  onMonthClick: PropTypes.func.isRequired,
  onDayClick: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isMatchesSection: PropTypes.bool,
};

CalendarComponent.defaultProps = {
  isMatchesSection: false,
};

export default CalendarComponent;
